'use client';

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useListVolunteerLocationsQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import type { VolunteerLocationItemDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import { useVolunteerQueryParams } from '../../utils/query-params.tsx';

// ─── Column Layout ───────────────────────────────────────────────────────────

/** Derive column counts from limit. */
export const buildColumnLayout = (limit: number) => ({
  columns: 1,
  columnsMd: Math.min(limit, 3),
  columnsLg: Math.min(limit, 3)
});

// ─── Hook ────────────────────────────────────────────────────────────────────

export type UseVolunteerListOptions = {
  q?: string;
  limit?: number;
  variant?: 'default' | 'tour';
  slug?: string;
  isDynamic?: boolean;
  onVolunteerLoad?: (info: {
    totalCount: number;
  }) => void;
  geoCoords?: {
    city?: string;
    state?: string;
    latitude?: number;
    longitude?: number;
  };
};
export const useVolunteerList = t0 => {
  const $ = _c(46);
  const {
    limit,
    variant: t1,
    slug,
    isDynamic: t2,
    onVolunteerLoad,
    geoCoords
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const isDynamic = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== limit) {
    t3 = {
      perPage: limit
    };
    $[0] = limit;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    volunteerFilters
  } = useVolunteerQueryParams(t3);
  const [currentPage, setCurrentPage] = useState(1);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [allResults, setAllResults] = useState(t4);
  const [pendingNewQuery, setPendingNewQuery] = useState(false);
  const sortBy = isDynamic ? volunteerFilters.sortby || "relevance" : "relevance";
  const resolvedLimit = isDynamic ? volunteerFilters.limit || limit || 12 : limit || 12;
  let t5;
  if ($[3] !== resolvedLimit) {
    t5 = buildColumnLayout(resolvedLimit);
    $[3] = resolvedLimit;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const columnLayout = t5;
  const fetchSize = resolvedLimit + 1;
  let apiSortBy = "relevance";
  if (sortBy === "location" && geoCoords?.latitude) {
    apiSortBy = "distance";
  } else {
    if (sortBy === "date") {
      apiSortBy = "date";
    }
  }
  const t6 = geoCoords?.latitude;
  const t7 = geoCoords?.longitude;
  const t8 = volunteerFilters.q || undefined;
  const t9 = (currentPage - 1) * resolvedLimit;
  const t10 = variant === "tour" && slug ? slug : undefined;
  let t11;
  if ($[5] !== apiSortBy || $[6] !== fetchSize || $[7] !== t10 || $[8] !== t6 || $[9] !== t7 || $[10] !== t8 || $[11] !== t9) {
    t11 = {
      latitude: t6,
      longitude: t7,
      sortBy: apiSortBy,
      query: t8,
      perPage: fetchSize,
      offset: t9,
      slug: t10
    };
    $[5] = apiSortBy;
    $[6] = fetchSize;
    $[7] = t10;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const t12 = pendingNewQuery ? undefined : keepPreviousData;
  let t13;
  if ($[13] !== t12) {
    t13 = {
      placeholderData: t12
    };
    $[13] = t12;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const volunteerQuery = useListVolunteerLocationsQuery(t11, t13);
  const {
    data: volunteerLocationsData,
    isLoading,
    isFetching,
    isError
  } = volunteerQuery;
  const totalCount = volunteerLocationsData?.totalCount || 0;
  const showLoading = isLoading || pendingNewQuery && isFetching;
  const geoLat = geoCoords?.latitude;
  const geoLng = geoCoords?.longitude;
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setCurrentPage(1);
      setPendingNewQuery(true);
    };
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== geoLat || $[17] !== geoLng || $[18] !== volunteerFilters.q || $[19] !== volunteerFilters.sortby) {
    t15 = [volunteerFilters.sortby, volunteerFilters.q, geoLat, geoLng];
    $[16] = geoLat;
    $[17] = geoLng;
    $[18] = volunteerFilters.q;
    $[19] = volunteerFilters.sortby;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[21] !== currentPage || $[22] !== resolvedLimit || $[23] !== volunteerLocationsData) {
    t16 = () => {
      if (!volunteerLocationsData?.items) {
        return;
      }
      const newResults = volunteerLocationsData.items.slice(0, resolvedLimit);
      if (currentPage === 1) {
        setAllResults(newResults);
      } else {
        setAllResults(prev => [...prev, ...newResults]);
      }
      setPendingNewQuery(false);
    };
    t17 = [volunteerLocationsData, currentPage, resolvedLimit];
    $[21] = currentPage;
    $[22] = resolvedLimit;
    $[23] = volunteerLocationsData;
    $[24] = t16;
    $[25] = t17;
  } else {
    t16 = $[24];
    t17 = $[25];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[26] !== isLoading || $[27] !== onVolunteerLoad || $[28] !== totalCount) {
    t18 = () => {
      if (onVolunteerLoad && !isLoading) {
        onVolunteerLoad({
          totalCount
        });
      }
    };
    t19 = [totalCount, isLoading, onVolunteerLoad];
    $[26] = isLoading;
    $[27] = onVolunteerLoad;
    $[28] = totalCount;
    $[29] = t18;
    $[30] = t19;
  } else {
    t18 = $[29];
    t19 = $[30];
  }
  useEffect(t18, t19);
  let t20;
  if ($[31] !== allResults) {
    t20 = allResults.filter(_temp);
    $[31] = allResults;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const uniqueLocations = t20;
  let t21;
  if ($[33] !== volunteerLocationsData?.items) {
    t21 = volunteerLocationsData?.items ?? [];
    $[33] = volunteerLocationsData?.items;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  const lastPageItems = t21;
  const hasMore = lastPageItems.length > resolvedLimit;
  let t22;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => {
      setCurrentPage(_temp2);
    };
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  const handleLoadMore = t22;
  const t23 = isFetching && currentPage > 1;
  let t24;
  if ($[36] !== columnLayout || $[37] !== hasMore || $[38] !== isError || $[39] !== resolvedLimit || $[40] !== showLoading || $[41] !== t23 || $[42] !== totalCount || $[43] !== uniqueLocations || $[44] !== volunteerLocationsData) {
    t24 = {
      locations: uniqueLocations,
      volunteerLocationsData,
      isLoading: showLoading,
      isLoadingMore: t23,
      isError,
      totalCount,
      resolvedLimit,
      columnLayout,
      hasMore,
      handleLoadMore
    };
    $[36] = columnLayout;
    $[37] = hasMore;
    $[38] = isError;
    $[39] = resolvedLimit;
    $[40] = showLoading;
    $[41] = t23;
    $[42] = totalCount;
    $[43] = uniqueLocations;
    $[44] = volunteerLocationsData;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  return t24;
};
function _temp(a, i, arr) {
  return arr.findIndex(b => b.eventId === a.eventId) === i;
}
function _temp2(prev_0) {
  return prev_0 + 1;
}